/**
 * Administration AI — v1
 *
 * Call It In operational intelligence surface for admin/billing staff.
 * Focuses on:
 *   • Completed but uninvoiced jobs (ready to bill)
 *   • Failed QuickBooks syncs
 *   • Active jobs by status
 *   • Overdue / stuck / missing-POD jobs
 *
 * v1 is read-only. No autonomous actions are performed here.
 * The "Ask" bar is a keyword router — it maps natural-language phrases to
 * pre-built views. Real AI inference requires a backend /admin/ask endpoint
 * (documented in api/adminAI.js as a future stub).
 *
 * Brand: Call It In — operational internal surface.
 * This is NOT an AxisSync-branded screen.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "layouts/dashboards/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SyncProblemIcon from "@mui/icons-material/SyncProblem";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import {
  fetchAllTasksForAdmin,
  fetchQBSyncStatus,
  latestSyncAttemptByTask,
  isReadyToInvoice,
  hasFailedQBSync,
  isMissingPOD,
  isOverdueActive,
  getBillableAmount,
  qbSyncLabel,
} from "../api/adminAI";
import { askAI } from "../api/aiClient";
import { classifyQuery, buildAdminAiContext } from "../api/adminAIContext";
import { isTerminalTaskStatus, normalizeTaskStatus } from "../constants/operations";
import { relativeTime, formatAbsoluteTime } from "../utils/dispatchOps";

// Size of the live-screen snapshot sent with each question. The TMS adds its own
// database extract on the server, so this stays small (keeps the whole prompt
// well inside the local model's context window).
const LIVE_SNAPSHOT_CHARS = 3000;

// ─── View keys ─────────────────────────────────────────────────────────────────

const VIEWS = {
  SUMMARY:       "summary",
  BILLING_READY: "billing_ready",
  FAILED_SYNC:   "failed_sync",
  ACTIVE:        "active",
  MISSING_POD:   "missing_pod",
  OVERDUE:       "overdue",
};

const VIEW_META = {
  [VIEWS.BILLING_READY]: {
    label:   "Ready to Invoice",
    icon:    <ReceiptLongIcon fontSize="small" />,
    color:   "success",
    caption: "Completed jobs with no QB invoice linked",
  },
  [VIEWS.FAILED_SYNC]: {
    label:   "Failed QB Sync",
    icon:    <SyncProblemIcon fontSize="small" />,
    color:   "error",
    caption: "QuickBooks sync attempts that failed",
  },
  [VIEWS.ACTIVE]: {
    label:   "Active Jobs",
    icon:    <LocalShippingIcon fontSize="small" />,
    color:   "primary",
    caption: "Jobs currently in-flight",
  },
  [VIEWS.MISSING_POD]: {
    label:   "Missing POD",
    icon:    <ImageNotSupportedIcon fontSize="small" />,
    color:   "warning",
    caption: "Delivered jobs without proof on file",
  },
  [VIEWS.OVERDUE]: {
    label:   "Overdue / Stuck",
    icon:    <WarningAmberIcon fontSize="small" />,
    color:   "error",
    caption: "Active jobs past their delivery deadline",
  },
};

// ─── View router ───────────────────────────────────────────────────────────────
// Only SHORT, UNAMBIGUOUS view commands ("what's ready to bill?", "failed
// syncs", "overdue") open a pre-built view; every other question goes to the
// AI. The matching rules live in api/adminAIContext.js (classifyQuery) so they
// are unit-tested. The old substring matcher captured any sentence containing
// e.g. "late", "current" or "qb" and never let it reach the AI.
function resolveQueryToView(raw) {
  const key = classifyQuery(raw);
  return Object.values(VIEWS).includes(key) ? key : null;
}

// ─── Formatting helpers ────────────────────────────────────────────────────────

function fmt$(amount) {
  if (amount == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function taskClientLabel(task) {
  return task.clientName || task.hospitalName || task.companyName || "—";
}

function taskRouteLabel(task) {
  const pickup   = task.pickupFacilityName || task.pickupAddress || "";
  const dropoff  = task.dropoffFacilityName || task.hospitalName || task.dropoffAddress || "";
  if (pickup && dropoff) return `${pickup} → ${dropoff}`;
  return pickup || dropoff || "—";
}

function taskDeadlineIso(task) {
  return (
    task.deliveryDeadline ||
    task.delivery_deadline ||
    task.milestones?.deliveryDeadline ||
    task.caseDeadline ||
    task.surgeryTime ||
    null
  );
}

// ─── Status chip ──────────────────────────────────────────────────────────────

const STATUS_CHIP_COLOR = {
  delivered: "success",
  completed: "success",
  canceled:  "default",
  failed:    "error",
  assigned:  "info",
  "en route": "warning",
  arrived:   "secondary",
  "picked up": "warning",
};

function StatusChip({ status }) {
  const norm  = normalizeTaskStatus(status);
  const color = STATUS_CHIP_COLOR[norm] || "default";
  return (
    <Chip
      size="small"
      label={status || "—"}
      color={color}
      variant="outlined"
      sx={{ fontWeight: 600, fontSize: 11 }}
    />
  );
}

// ─── Summary stat card ────────────────────────────────────────────────────────

function SummaryCard({ label, icon, value, caption, color, active, onClick }) {
  return (
    <Card
      elevation={0}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); }
      }}
      sx={{
        cursor: "pointer",
        borderRadius: 3,
        height: "100%",
        border: (theme) =>
          `1px solid ${
            active
              ? theme.palette[color]?.main || theme.palette.primary.main
              : theme.palette.divider
          }`,
        boxShadow: active ? `0 0 0 2px rgba(25,118,210,0.08)` : "none",
        bgcolor: active ? `${color}.50` : "background.paper",
        transition: "border-color 0.15s, box-shadow 0.15s",
        "&:hover": {
          borderColor: (theme) =>
            theme.palette[color]?.main || theme.palette.primary.main,
        },
      }}
    >
      <CardContent sx={{ pb: "16px !important" }}>
        <Box display="flex" alignItems="center" gap={0.75} mb={0.5}>
          <Box
            sx={{
              color: active ? `${color}.main` : "text.secondary",
              display: "flex",
              alignItems: "center",
            }}
          >
            {icon}
          </Box>
          <Typography
            variant="caption"
            color={active ? `${color}.main` : "text.secondary"}
            sx={{ fontWeight: 700, letterSpacing: 0.3 }}
          >
            {label}
          </Typography>
        </Box>
        <Typography
          variant="h4"
          sx={{ mt: 0.25, fontWeight: 800 }}
          color={active ? `${color}.main` : "text.primary"}
        >
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      </CardContent>
    </Card>
  );
}

// ─── Task table ───────────────────────────────────────────────────────────────

const TABLE_COLS = {
  [VIEWS.BILLING_READY]: ["Customer", "Route", "Status", "Billable", "QB Sync", "Completed", "Action"],
  [VIEWS.FAILED_SYNC]:   ["Customer", "Route", "Status", "Billable", "QB Sync", "Last Update", "Action"],
  [VIEWS.ACTIVE]:        ["Customer", "Route", "Status", "Driver", "Deadline", "Last Update", "Action"],
  [VIEWS.MISSING_POD]:   ["Customer", "Route", "Status", "Billable", "Delivered", "Action"],
  [VIEWS.OVERDUE]:       ["Customer", "Route", "Status", "Driver", "Deadline", "Overdue By", "Action"],
};

function TaskTable({ view, tasks, nowTick, onOpenTask, syncLogByTask }) {
  const cols = TABLE_COLS[view] || [];

  if (tasks.length === 0) {
    return (
      <Alert severity="success" sx={{ borderRadius: 2, mt: 1 }}>
        No items match this filter. Operations look clean for this category.
      </Alert>
    );
  }

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: 640 }}>
        <TableHead>
          <TableRow>
            {cols.map((c) => (
              <TableCell key={c} sx={{ fontWeight: 700, fontSize: 11, color: "text.secondary", whiteSpace: "nowrap" }}>
                {c}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {tasks.slice(0, 100).map((task) => (
            <TableRow
              key={task.id}
              hover
              sx={{ "&:last-child td": { borderBottom: 0 } }}
            >
              <TableCell sx={{ fontWeight: 600, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <Tooltip title={taskClientLabel(task)}>
                  <span>{taskClientLabel(task)}</span>
                </Tooltip>
              </TableCell>

              <TableCell sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12 }}>
                <Tooltip title={taskRouteLabel(task)}>
                  <span>{taskRouteLabel(task)}</span>
                </Tooltip>
              </TableCell>

              <TableCell>
                <StatusChip status={task.status} />
              </TableCell>

              {/* Columns that differ by view */}
              {(view === VIEWS.BILLING_READY || view === VIEWS.FAILED_SYNC || view === VIEWS.MISSING_POD) && (
                <TableCell sx={{ fontWeight: 600, color: "success.main", fontSize: 13 }}>
                  {fmt$(getBillableAmount(task))}
                </TableCell>
              )}

              {(view === VIEWS.BILLING_READY || view === VIEWS.FAILED_SYNC) && (
                <TableCell>
                  {(() => {
                    const logEntry = syncLogByTask?.get(task.id);
                    const chip = (
                      <Chip
                        size="small"
                        label={qbSyncLabel(task)}
                        color={hasFailedQBSync(task) ? "error" : "default"}
                        variant={hasFailedQBSync(task) ? "filled" : "outlined"}
                        sx={{ fontSize: 11 }}
                      />
                    );
                    if (view === VIEWS.FAILED_SYNC && logEntry) {
                      const tip = [
                        logEntry.error_msg ? `Error: ${logEntry.error_msg}` : null,
                        logEntry.attempted_at ? `Attempted: ${fmtDate(logEntry.attempted_at)}` : null,
                        logEntry.triggered_by ? `By: ${logEntry.triggered_by}` : null,
                      ].filter(Boolean).join(" · ");
                      return tip ? <Tooltip title={tip}><span>{chip}</span></Tooltip> : chip;
                    }
                    return chip;
                  })()}
                </TableCell>
              )}

              {(view === VIEWS.ACTIVE || view === VIEWS.OVERDUE) && (
                <TableCell sx={{ fontSize: 12, maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {task.driver || task.assignedDriver || <Typography variant="caption" color="error.main" fontWeight={700}>UNASSIGNED</Typography>}
                </TableCell>
              )}

              {(view === VIEWS.ACTIVE || view === VIEWS.OVERDUE) && (
                <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>
                  {(() => {
                    const dl = taskDeadlineIso(task);
                    if (!dl) return <Typography variant="caption" color="text.disabled">No deadline</Typography>;
                    const past = new Date(dl) < new Date();
                    return (
                      <Typography variant="caption" fontWeight={600} color={past ? "error.main" : "text.primary"}>
                        {fmtDate(dl)}
                      </Typography>
                    );
                  })()}
                </TableCell>
              )}

              {view === VIEWS.OVERDUE && (
                <TableCell sx={{ fontSize: 12 }}>
                  {(() => {
                    const dl = taskDeadlineIso(task);
                    if (!dl) return "—";
                    const diffMs = Date.now() - new Date(dl).getTime();
                    const hrs = Math.floor(diffMs / 3_600_000);
                    const mins = Math.floor((diffMs % 3_600_000) / 60_000);
                    return (
                      <Typography variant="caption" color="error.main" fontWeight={700}>
                        {hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`}
                      </Typography>
                    );
                  })()}
                </TableCell>
              )}

              {(view === VIEWS.BILLING_READY || view === VIEWS.ACTIVE || view === VIEWS.FAILED_SYNC) && (
                <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>
                  {relativeTime(
                    (view === VIEWS.FAILED_SYNC && syncLogByTask?.get(task.id)?.attempted_at) ||
                      task.updatedAt || task.updated_at || task.createdAt || task.created_at,
                    nowTick
                  )}
                </TableCell>
              )}

              {view === VIEWS.MISSING_POD && (
                <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>
                  {fmtDate(task.updatedAt || task.updated_at || task.podReceivedAt)}
                </TableCell>
              )}

              <TableCell>
                <Button
                  size="small"
                  variant="text"
                  endIcon={<OpenInNewIcon sx={{ fontSize: "14px !important" }} />}
                  onClick={() => onOpenTask(task)}
                  sx={{ fontSize: 11, whiteSpace: "nowrap" }}
                >
                  Open
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {tasks.length > 100 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", p: 1.5 }}>
          Showing first 100 of {tasks.length} results.
        </Typography>
      )}
    </Box>
  );
}

// ─── Backend gap notice ───────────────────────────────────────────────────────

function BackendGapNotice({ view, syncLogLoaded }) {
  const gaps = {
    [VIEWS.BILLING_READY]: {
      show: true,
      message:
        "Billing-ready status is derived from task fields (invoiceId, qbSyncStatus). " +
        "For a server-authoritative uninvoiced list, add GET /tasks/billing-ready " +
        "to the backend (see src/api/adminAI.js).",
    },
    [VIEWS.FAILED_SYNC]: syncLogLoaded
      ? {
          show: true,
          message:
            "Hover a \"Sync failed\" chip to see the exact error message, attempt time, " +
            "and who triggered it — pulled live from GET /invoices/sync/status.",
        }
      : {
          show: true,
          message:
            "Live QuickBooks sync log (GET /invoices/sync/status) could not be loaded, so this " +
            "view is falling back to the task.qbSyncStatus field only — no error detail available.",
        },
    [VIEWS.MISSING_POD]: {
      show: true,
      message:
        "Missing POD detection checks task.podPhotoReceived, task.podSignatureReceived, " +
        "and task.podReceivedAt. Add GET /tasks/missing-pod for a server-authoritative list.",
    },
  };

  const gap = gaps[view];
  if (!gap?.show) return null;

  return (
    <Alert
      severity="info"
      icon={false}
      sx={{
        borderRadius: 2,
        mb: 2,
        bgcolor: "grey.50",
        border: (t) => `1px solid ${t.palette.divider}`,
        "& .MuiAlert-message": { fontSize: 12 },
      }}
    >
      <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.25 }}>
        DATA SOURCE NOTE
      </Typography>
      {gap.message}
    </Alert>
  );
}

// ─── Quick prompts ────────────────────────────────────────────────────────────

const QUICK_PROMPTS = [
  { label: "What's ready to bill?",   view: VIEWS.BILLING_READY, icon: "💳" },
  { label: "Failed QB syncs",         view: VIEWS.FAILED_SYNC,   icon: "⚠️" },
  { label: "Show active jobs",        view: VIEWS.ACTIVE,        icon: "🚚" },
  { label: "Missing proof of delivery", view: VIEWS.MISSING_POD, icon: "📷" },
  { label: "Overdue / stuck jobs",    view: VIEWS.OVERDUE,       icon: "🕐" },
];

// ─── Main component ───────────────────────────────────────────────────────────

export default function AdminAI() {
  const navigate  = useNavigate();
  const loadedRef = useRef(false);
  const nowTickRef = useRef(Date.now());

  const [tasks,      setTasks]      = useState([]);
  const [syncLog,    setSyncLog]    = useState(null); // null = not loaded / unavailable
  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error,      setError]      = useState(null);
  const [lastSync,   setLastSync]   = useState(null);
  const [nowTick,    setNowTick]    = useState(Date.now());

  const [activeView,   setActiveView]   = useState(VIEWS.SUMMARY);
  const [askText,      setAskText]      = useState("");
  const [askFeedback,  setAskFeedback]  = useState(null); // null = no query run yet
  const [askBusy,      setAskBusy]      = useState(false);

  // ── Data loading ──────────────────────────────────────────────────────────

  const load = useCallback(async ({ background = false } = {}) => {
    if (background || loadedRef.current) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const [res, syncData] = await Promise.all([
        fetchAllTasksForAdmin(),
        fetchQBSyncStatus(),
      ]);
      if (!res.ok) throw new Error(`Tasks fetch failed (HTTP ${res.status}).`);
      const data = await res.json();
      setTasks(Array.isArray(data) ? data : []);
      setSyncLog(syncData); // null if the endpoint is unreachable — handled downstream
      setLastSync(new Date().toISOString());
      loadedRef.current = true;
      setError(null);
    } catch (e) {
      setError(e.message || "Could not load task data.");
    } finally {
      setLoading(false);
      setRefreshing(false);
      nowTickRef.current = Date.now();
      setNowTick(Date.now());
    }
  }, []);

  useEffect(() => {
    load();
    const pollId = setInterval(() => load({ background: true }), 30_000);
    const tickId = setInterval(() => setNowTick(Date.now()), 15_000);
    return () => {
      clearInterval(pollId);
      clearInterval(tickId);
    };
  }, [load]);

  // ── Derived task sets ─────────────────────────────────────────────────────

  const derived = useMemo(() => {
    const billingReady = tasks.filter(isReadyToInvoice);
    const failedSync   = tasks.filter(hasFailedQBSync);
    const active       = tasks.filter((t) => !isTerminalTaskStatus(t.status));
    const missingPOD   = tasks.filter(isMissingPOD);
    const overdue      = tasks.filter(isOverdueActive);

    const billingReadyTotal = billingReady.reduce((sum, t) => {
      const amt = getBillableAmount(t);
      return amt != null ? sum + amt : sum;
    }, 0);

    return { billingReady, failedSync, active, missingPOD, overdue, billingReadyTotal };
  }, [tasks]);

  const syncLogByTask = useMemo(
    () => latestSyncAttemptByTask(syncLog?.items),
    [syncLog]
  );

  // ── Ask bar handler ───────────────────────────────────────────────────────
  // Always produces a visible, explicit response — either a success
  // confirmation naming the view now shown, or a warning naming the
  // categories that are understood. Never a silent no-op.

  // Live snapshot of what is open on this screen right now (same predicates as
  // the tables). History - any date range, customer, driver, invoice, POD or
  // address-book question - is NOT built here: with tmsData the TMS backend
  // reads its own database for the question (read-only, by the caller's role)
  // and attaches that extract server-side. See api/adminAIContext.js and
  // backend ai_data.py. This browser snapshot carries no addresses, phone numbers,
  // notes or clinical detail; Address Book contact fields are added server-side
  // only for the matching saved records of a location/contact question.
  const buildAiContext = (question) =>
    buildAdminAiContext({ tasks, question, budget: LIVE_SNAPSHOT_CHARS });

  const handleAsk = async () => {
    const trimmed = askText.trim();
    if (!trimmed) {
      setAskFeedback(null);
      return;
    }

    // 1) Short, unambiguous view commands open the pre-built view - instant and
    //    always available. Anything else is a real question for the AI.
    const view = resolveQueryToView(trimmed);
    if (view) {
      setActiveView(view);
      setAskFeedback({
        severity: "success",
        message: `Showing "${VIEW_META[view].label}" — ${VIEW_META[view].caption}.`,
      });
      return;
    }

    // 2) Anything else goes to the AI assistant via the TMS backend
    //    (POST /ai/ask -> private gateway). Never blocks the keyword views.
    setAskBusy(true);
    setAskFeedback(null);
    try {
      // The backend adds the grounding / read-only instructions around the
      // snapshot, so the prompt is just the admin's question.
      const answer = await askAI(trimmed, buildAiContext(trimmed), { tmsData: true });
      setAskFeedback({
        severity: answer ? "info" : "warning",
        message:
          answer ||
          "The AI assistant did not return an answer (its reply may have been cut off). " +
            "Try a narrower question, e.g. name a customer or driver.",
        ai: !!answer,
        basis: answer
          ? `Based on a read-only look-up of the TMS database for your question, plus the ${tasks.length} tasks loaded on this screen${
              lastSync ? ` as of ${formatAbsoluteTime(lastSync)}` : ""
            }`
          : null,
      });
    } catch (e) {
      setAskFeedback({
        severity: "warning",
        message: `The AI assistant is unavailable right now (${e.message}). You can still use the views: billing, sync, active, POD, or overdue.`,
      });
    } finally {
      setAskBusy(false);
    }
  };

  // Mirrors the Ask button's guard (no-op on empty/whitespace input) so
  // pressing Enter in an empty field can't silently do nothing differently
  // than clicking the disabled button would.
  const handleAskKeyDown = (e) => {
    if (e.key === "Enter" && askText.trim() && !askBusy) handleAsk();
  };

  // ── Navigate to task ──────────────────────────────────────────────────────

  const openTask = useCallback(
    (task) => {
      try {
        window.sessionStorage.setItem("ci_focus_task_id", task.id);
      } catch {
        // ignore
      }
      navigate("/applications/task-list");
    },
    [navigate]
  );

  // ── Active view task list ─────────────────────────────────────────────────

  const viewTasks = useMemo(() => {
    switch (activeView) {
      case VIEWS.BILLING_READY: return derived.billingReady;
      case VIEWS.FAILED_SYNC:   return derived.failedSync;
      case VIEWS.ACTIVE:        return derived.active;
      case VIEWS.MISSING_POD:   return derived.missingPOD;
      case VIEWS.OVERDUE:       return derived.overdue;
      default:                  return [];
    }
  }, [activeView, derived]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box px={2} py={3}>

        {/* ── Page header ───────────────────────────────────────────────── */}
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1.5}
          mb={3}
        >
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={0.25}>
              <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: -0.3 }}>
                Administration AI
              </Typography>
              <Chip
                label="v1"
                size="small"
                color="default"
                sx={{ fontWeight: 700, fontSize: 10, height: 18 }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              Billing visibility and operational status — Call It In
            </Typography>
            <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
              {lastSync
                ? `Last refreshed ${relativeTime(lastSync, nowTick)} · ${formatAbsoluteTime(lastSync)}`
                : "Loading…"}
              {refreshing ? " · updating…" : ""}
            </Typography>
          </Box>

          <Button
            size="small"
            variant="outlined"
            startIcon={loading || refreshing ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
            onClick={() => load({ background: true })}
            disabled={loading || refreshing}
            sx={{ alignSelf: "flex-start", mt: 0.5 }}
          >
            {loading || refreshing ? "Refreshing…" : "Refresh"}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* ── Ask bar ───────────────────────────────────────────────────── */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: (t) => `1px solid ${t.palette.divider}`,
            mb: 3,
            background: (t) =>
              t.palette.mode === "dark"
                ? "linear-gradient(135deg, #1a1f2e 0%, #1e2534 100%)"
                : "linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%)",
          }}
        >
          <CardContent>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Ask Administration AI
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              {
                "Ask in plain language, e.g. \u201CWhich customers have the most unfinished jobs?\u201D or " +
                "\u201CDoes Dana have anything overdue?\u201D, \u201CWhat did we invoice Zimmer in September?\u201D or " +
                "\u201CHow many hospitals are in the address book?\u201D. Short commands like \u201Cfailed syncs\u201D or " +
                "\u201Cready to bill\u201D open the matching pre-built view; everything else is answered by the AI " +
                "assistant using a read-only look-up of the TMS (tasks, invoices, customers, drivers, proof of delivery, " +
                "address book) for the dates and names in your question. Advisory only: it cannot change anything. " +
                "Address Book contact details (e.g. \u201CWhat is Elizabeth Matthews\u2019 phone number?\u201D) are looked up " +
                "from the saved records only when your question asks for them; clinical detail, job notes and proof " +
                "images are never sent."
              }
            </Typography>

            <Box display="flex" gap={1.5} alignItems="center">
              <TextField
                fullWidth
                size="small"
                placeholder={'e.g. "What\'s ready to bill?" or "Show failed syncs"'}
                value={askText}
                onChange={(e) => { setAskText(e.target.value); setAskFeedback(null); }}
                onKeyDown={handleAskKeyDown}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="disabled" />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 2, bgcolor: "background.paper" },
                }}
              />
              <Button
                variant="contained"
                disableElevation
                onClick={handleAsk}
                sx={{ borderRadius: 2, whiteSpace: "nowrap", minWidth: 80 }}
                disabled={!askText.trim() || askBusy}
              >
                {askBusy ? <CircularProgress size={16} color="inherit" /> : "Ask"}
              </Button>
            </Box>

            {askFeedback && (
              <Alert
                severity={askFeedback.severity}
                sx={{ mt: 1.5, borderRadius: 2, whiteSpace: "pre-wrap" }}
                onClose={() => setAskFeedback(null)}
              >
                {askFeedback.ai && <strong>AI assistant (advisory): </strong>}
                {askFeedback.message}
                {askFeedback.basis && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    {askFeedback.basis}. Advisory only: verify against the tables below before acting.
                  </Typography>
                )}
              </Alert>
            )}

            {/* Quick prompts */}
            <Box display="flex" flexWrap="wrap" gap={1} mt={2}>
              {QUICK_PROMPTS.map((p) => (
                <Chip
                  key={p.view}
                  label={`${p.icon} ${p.label}`}
                  onClick={() => {
                    setAskText(p.label);
                    setActiveView(p.view);
                    setAskFeedback({
                      severity: "success",
                      message: `Showing "${VIEW_META[p.view].label}" — ${VIEW_META[p.view].caption}.`,
                    });
                  }}
                  clickable
                  variant={activeView === p.view ? "filled" : "outlined"}
                  color={activeView === p.view ? "primary" : "default"}
                  size="small"
                  sx={{ fontWeight: 600, fontSize: 12 }}
                />
              ))}
              {activeView !== VIEWS.SUMMARY && (
                <Chip
                  label="✕ Clear"
                  size="small"
                  onClick={() => {
                    setActiveView(VIEWS.SUMMARY);
                    setAskText("");
                    setAskFeedback(null);
                  }}
                  clickable
                  variant="outlined"
                  sx={{ fontSize: 12 }}
                />
              )}
            </Box>
          </CardContent>
        </Card>

        {/* ── Summary cards ─────────────────────────────────────────────── */}
        {loading && !loadedRef.current ? (
          <Box py={6} textAlign="center">
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading operational data…
            </Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <SummaryCard
                  label="Ready to Invoice"
                  icon={<ReceiptLongIcon fontSize="small" />}
                  value={derived.billingReady.length}
                  caption={
                    derived.billingReadyTotal > 0
                      ? `${fmt$(derived.billingReadyTotal)} unbilled`
                      : "No billable amount recorded"
                  }
                  color="success"
                  active={activeView === VIEWS.BILLING_READY}
                  onClick={() => setActiveView(VIEWS.BILLING_READY)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <SummaryCard
                  label="Failed QB Sync"
                  icon={<SyncProblemIcon fontSize="small" />}
                  value={derived.failedSync.length}
                  caption="QuickBooks errors"
                  color="error"
                  active={activeView === VIEWS.FAILED_SYNC}
                  onClick={() => setActiveView(VIEWS.FAILED_SYNC)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <SummaryCard
                  label="Active Jobs"
                  icon={<LocalShippingIcon fontSize="small" />}
                  value={derived.active.length}
                  caption="Currently in-flight"
                  color="primary"
                  active={activeView === VIEWS.ACTIVE}
                  onClick={() => setActiveView(VIEWS.ACTIVE)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <SummaryCard
                  label="Missing POD"
                  icon={<ImageNotSupportedIcon fontSize="small" />}
                  value={derived.missingPOD.length}
                  caption="Delivered, no proof"
                  color="warning"
                  active={activeView === VIEWS.MISSING_POD}
                  onClick={() => setActiveView(VIEWS.MISSING_POD)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <SummaryCard
                  label="Overdue / Stuck"
                  icon={<WarningAmberIcon fontSize="small" />}
                  value={derived.overdue.length}
                  caption="Past delivery deadline"
                  color="error"
                  active={activeView === VIEWS.OVERDUE}
                  onClick={() => setActiveView(VIEWS.OVERDUE)}
                />
              </Grid>
            </Grid>

            {/* ── Detail panel ────────────────────────────────────────────── */}
            {activeView !== VIEWS.SUMMARY && (
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: (t) => `1px solid ${t.palette.divider}`,
                }}
              >
                <CardContent>
                  {/* Panel header */}
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap={1}
                    mb={2}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          color: (t) =>
                            t.palette[VIEW_META[activeView]?.color]?.main ||
                            t.palette.primary.main,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {VIEW_META[activeView]?.icon}
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {VIEW_META[activeView]?.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {VIEW_META[activeView]?.caption} ·{" "}
                          <strong>{viewTasks.length}</strong> result
                          {viewTasks.length !== 1 ? "s" : ""}
                        </Typography>
                      </Box>
                    </Box>

                    <Button
                      size="small"
                      onClick={() => {
                        setActiveView(VIEWS.SUMMARY);
                        setAskText("");
                      }}
                    >
                      Clear
                    </Button>
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  <BackendGapNotice view={activeView} syncLogLoaded={!!syncLog} />

                  <TaskTable
                    view={activeView}
                    tasks={viewTasks}
                    nowTick={nowTick}
                    onOpenTask={openTask}
                    syncLogByTask={syncLogByTask}
                  />
                </CardContent>
              </Card>
            )}

            {/* ── Summary state (no view selected) ─────────────────────── */}
            {activeView === VIEWS.SUMMARY && (
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: (t) => `1px solid ${t.palette.divider}`,
                  bgcolor: "grey.50",
                }}
              >
                <CardContent sx={{ py: 3 }}>
                  <Box textAlign="center" py={1}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Select a summary card above or use the Ask bar to drill into a category.
                    </Typography>
                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      justifyContent="center"
                      gap={1}
                    >
                      {Object.entries(VIEW_META).map(([key, meta]) => (
                        <Button
                          key={key}
                          size="small"
                          variant="outlined"
                          startIcon={meta.icon}
                          onClick={() => setActiveView(key)}
                          color={meta.color}
                          sx={{ borderRadius: 2 }}
                        >
                          {meta.label}
                        </Button>
                      ))}
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* ── Backend gaps reference ────────────────────────────────────── */}
        <Box mt={3}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              border: (t) => `1px solid ${t.palette.divider}`,
              bgcolor: "grey.50",
            }}
          >
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.75 }}>
                Backend endpoints needed for live billing intelligence
              </Typography>
              <Stack spacing={0.75}>
                {[
                  { endpoint: "GET /invoices/sync/status", desc: "QuickBooks sync attempt log with error details and timestamps", live: true },
                  { endpoint: "GET /tasks/billing-ready", desc: "Server-authoritative list of completed, uninvoiced jobs" },
                  { endpoint: "GET /tasks/missing-pod", desc: "Delivered tasks without a linked proof record" },
                  { endpoint: "GET /admin/billing-summary", desc: "Aggregated counts and totals for the summary cards" },
                  { endpoint: "POST /admin/ask", desc: "Future: natural-language billing query endpoint for real AI inference" },
                ].map((row) => (
                  <Box key={row.endpoint} display="flex" gap={1.5} alignItems="flex-start">
                    <Chip
                      label={row.endpoint}
                      size="small"
                      variant="outlined"
                      color={row.live ? "success" : "default"}
                      sx={{ fontFamily: "monospace", fontSize: 11, flexShrink: 0 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ pt: 0.25 }}>
                      {row.desc}
                      {row.live ? " — live, connected." : ""}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Box>

      </Box>
    </DashboardLayout>
  );
}
